import type { AdvanceGate } from '../../../shared/onboarding/missionTypes';
import { TOUR_DOM_EVENTS } from '../../onboarding/onboardingDomEvents';

export type MissionAdvanceCallbacks = {
  onStepComplete: (stepIndex: number) => void;
  onComplete: () => void;
  setStepIndex: (index: number) => void;
};

export function anchorSelector(anchor: string): string {
  return `[data-tour="${anchor}"], [data-tour-stable="${anchor}"]`;
}

export function pathMatchesPattern(pathname: string, pattern: string): boolean {
  if (pattern.endsWith('/')) {
    return pathname.includes(pattern);
  }
  return pathname.includes(pattern);
}

/** Whether the mission panel / spotlight should offer a manual Next/Continue control. */
export function shouldShowMissionManualNext(
  gate: AdvanceGate,
  options: { targetReady: boolean; targetOccluded: boolean },
): boolean {
  if (gate.type === 'manual') {
    return true;
  }
  if (gate.type === 'click') {
    return options.targetOccluded;
  }
  // Route, DOM, and stepper gates highlight informational targets — advance manually once visible.
  return options.targetReady;
}

export function isMissionAdvanceGateSatisfied(
  gate: AdvanceGate,
  pathname: string = typeof window !== 'undefined'
    ? window.location.pathname
    : '',
): boolean {
  if (gate.type === 'route') {
    return pathMatchesPattern(pathname, gate.pathPattern);
  }
  return false;
}

export function clickGateSelector(
  gate: Extract<AdvanceGate, { type: 'click' }>,
  rowAnchor?: string,
): string {
  const base = anchorSelector(gate.anchor);
  if (rowAnchor) {
    return `${anchorSelector(rowAnchor)} ${base}`;
  }
  return base;
}

export function subscribeAdvanceGate(
  gate: AdvanceGate,
  onSatisfied: () => boolean | void,
  options?: { rowAnchor?: string },
): () => void {
  let satisfied = false;
  const once = () => {
    if (satisfied) {
      return;
    }
    const result = onSatisfied();
    if (result === false) {
      return;
    }
    satisfied = true;
  };

  if (gate.type === 'manual') {
    return () => undefined;
  }

  if (gate.type === 'click') {
    const selector = clickGateSelector(gate, options?.rowAnchor);
    const handler = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest(selector)) {
        once();
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }

  if (gate.type === 'route') {
    const check = () => {
      if (pathMatchesPattern(window.location.pathname, gate.pathPattern)) {
        once();
      }
    };
    check();
    window.addEventListener('popstate', check);
    const interval = window.setInterval(check, 400);
    return () => {
      window.removeEventListener('popstate', check);
      window.clearInterval(interval);
    };
  }

  if (gate.type === 'dom') {
    const handler = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }
      if (event.type !== gate.event) {
        return;
      }
      if (gate.detail?.modalId) {
        const detail = event.detail as { modalId?: string };
        if (detail.modalId !== gate.detail.modalId) {
          return;
        }
      }
      once();
    };
    window.addEventListener(gate.event, handler as EventListener);
    return () => window.removeEventListener(gate.event, handler as EventListener);
  }

  if (gate.type === 'stepper') {
    const handler = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }
      const detail = event.detail as { stepIndex?: number };
      if (detail.stepIndex === gate.stepIndex) {
        once();
      }
    };
    window.addEventListener(
      TOUR_DOM_EVENTS.STEPPER_STEP_CHANGED,
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        TOUR_DOM_EVENTS.STEPPER_STEP_CHANGED,
        handler as EventListener,
      );
  }

  return () => undefined;
}
